import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface LearnerResult {
  id: string;
  full_name: string | null;
  email: string | null;
  avatar_url: string | null;
  role: string | null;
}

export function useLearnerSearch(searchTerm: string) {
  const [debouncedTerm, setDebouncedTerm] = useState(searchTerm);

  // Debounce search term by 300ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const query = useQuery({
    queryKey: ["learner_search", debouncedTerm],
    queryFn: async (): Promise<LearnerResult[]> => {
      if (!debouncedTerm || debouncedTerm.length < 2) {
        return [];
      }

      // Use admin RPC to search users by name or email
      const { data, error } = await supabase.rpc("admin_search_users_for_deletion", {
        search_query: debouncedTerm,
        include_no_name: false,
      });

      if (error) {
        console.error("Error searching learners:", error);
        return [];
      }

      // Map to LearnerResult format and limit to 10
      return (data || []).slice(0, 10).map((user) => ({
        id: user.id,
        full_name: user.full_name,
        email: user.email,
        avatar_url: user.avatar_url,
        role: user.role,
      }));
    },
    enabled: debouncedTerm.length >= 2,
  });

  return {
    results: query.data || [],
    isLoading: query.isLoading,
    isSearching: searchTerm !== debouncedTerm,
  };
}
